import {
  Component, signal, ViewChild, ElementRef,
  AfterViewChecked, inject, OnInit, OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { marked } from 'marked';
import { AgentChatService } from '../../services/agent-chat.service';

export interface ChatMessage {
  role: 'user' | 'agent';
  text: string;
  renderedHtml?: string;
  timestamp: Date;
}

@Component({
  selector: 'app-agent-chat',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './agent-chat.component.html',
  styleUrls: ['./agent-chat.component.scss']
})
export class AgentChatComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('messagesContainer') private _messagesContainer!: ElementRef<HTMLElement>;

  private readonly _service = inject(AgentChatService);

  isOpen = signal(false);
  isDocked = signal(false);
  panelWidth = signal(420);
  messages = signal<ChatMessage[]>([]);
  inputText = signal('');
  isLoading = signal(false);
  sessionId = '';

  private readonly _minWidth = 280;
  private readonly _maxWidth = 860;
  private _dragCleanup: (() => void) | null = null;
  private _shouldScrollToBottom = false;

  ngOnInit(): void {
    this.sessionId = crypto.randomUUID();
  }

  ngOnDestroy(): void {
    this._dragCleanup?.();
    document.body.style.removeProperty('--agent-chat-docked-width');
  }

  ngAfterViewChecked(): void {
    if (this._shouldScrollToBottom) {
      this._scrollToBottom();
      this._shouldScrollToBottom = false;
    }
  }

  togglePanel(): void {
    this.isOpen.update(v => !v);
    if (!this.isOpen() && this.isDocked()) {
      this.isDocked.set(false);
      document.body.style.removeProperty('--agent-chat-docked-width');
    }
  }

  setDocked(docked: boolean): void {
    this.isDocked.set(docked);
    if (docked) {
      this.isOpen.set(true);
      document.body.style.setProperty('--agent-chat-docked-width', this.panelWidth() + 'px');
    } else {
      document.body.style.removeProperty('--agent-chat-docked-width');
    }
  }

  onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    const onMove = (e: MouseEvent) => {
      const newWidth = Math.min(this._maxWidth, Math.max(this._minWidth, window.innerWidth - e.clientX));
      this.panelWidth.set(newWidth);
      document.body.style.setProperty('--agent-chat-docked-width', newWidth + 'px');
      window.dispatchEvent(new Event('resize'));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      this._dragCleanup = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    this._dragCleanup = onUp;
  }

  onInputChange(value: string): void {
    this.inputText.set(value);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  sendMessage(): void {
    const question = this.inputText().trim();
    if (!question || this.isLoading()) return;

    const userMsg: ChatMessage = {
      role: 'user',
      text: question,
      timestamp: new Date()
    };
    this.messages.update(msgs => [...msgs, userMsg]);
    this.inputText.set('');
    this.isLoading.set(true);
    this._shouldScrollToBottom = true;

    this._service.ask({ question, sessionId: this.sessionId }).subscribe({
      next: (res) => {
        const answer = res.success
          ? res.answer
          : (res.errorMessage ?? 'An error occurred. Please try again.');

        const rendered = marked.parse(answer) as string;

        const agentMsg: ChatMessage = {
          role: 'agent',
          text: answer,
          renderedHtml: rendered,
          timestamp: new Date()
        };
        this.messages.update(msgs => [...msgs, agentMsg]);
        this.isLoading.set(false);
        this._shouldScrollToBottom = true;
      },
      error: () => {
        const errMsg: ChatMessage = {
          role: 'agent',
          text: 'Failed to reach the agent. Please check your connection and try again.',
          timestamp: new Date()
        };
        this.messages.update(msgs => [...msgs, errMsg]);
        this.isLoading.set(false);
        this._shouldScrollToBottom = true;
      }
    });
  }

  clearConversation(): void {
    this.messages.set([]);
    this.sessionId = crypto.randomUUID();
  }

  private _scrollToBottom(): void {
    try {
      const el = this._messagesContainer?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    } catch {}
  }
}
